import { Avatar } from "@dropbox/dig-components/avatar";
import { IconButton } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { EditLine } from "@dropbox/dig-icons/assets";
import { getInitials } from "@mirage/shared/account";
import i18n from "@mirage/translations";
import cx from "classnames";
import { forwardRef } from "react";

import type { AvatarSizes } from "@dropbox/dig-components/avatar";
import styles from "./UserProfileAvatar.module.css";

type ProfileAvatarProps = {
  user: {
    name: string;
    profile_photo_url: string | null | undefined;
    on_leave?: boolean;
    is_deactivated?: boolean;
  };
  size?: AvatarSizes | "profile" | "customize_large";
  className?: string;
  style?: React.CSSProperties;
  onEditClick?: () => void;
};

/**
 * Avatar component for displaying user a profile photo or initials
 *
 * @param user - user info
 * @param size - size of avatar
 * @param className - custom className for avatar
 * @param style - custom style for avatar
 */
export const UserProfileAvatar = forwardRef<HTMLElement, ProfileAvatarProps>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({ user, size, className, style, onEditClick }, ref: any) => {
    const userPhotoUrl = user.profile_photo_url;
    const hasPhotoUrl = !!userPhotoUrl?.length;

    const isCustomSize = ["profile", "customize_large"].includes(size ?? "");
    const employeeInitials = getInitials(user.name);

    return (
      <Box
        position="relative"
        ref={ref}
        className={cx(className, {
          [styles.avatar40]: size === "customize_large",
          [styles.avatar116]: size === "profile",
        })}
        style={style}
      >
        {user.on_leave && hasPhotoUrl && (
          <div className={styles.wrapper}>
            <div className={styles.ticker}>
              <div className={styles.tickerText}>
                <Text size="small">{i18n.t("currently_on_leave")}</Text>
              </div>
            </div>
          </div>
        )}
        <Box
          as={Avatar}
          src={user.profile_photo_url || ""}
          size={isCustomSize ? "large" : (size as AvatarSizes)}
          alt={user.name}
          isInactive={user.is_deactivated}
          className={cx(styles.avatar, {
            [styles.deactivatedAvatarLoaded]: user.is_deactivated,
          })}
        >
          {employeeInitials}
        </Box>
        {onEditClick && (
          <Box
            position="absolute"
            style={{
              background: "var(--color__faint__background)",
              borderRadius: "8px",
              bottom: "2px",
              left: "85px",
            }}
          >
            <IconButton
              variant="opacity"
              shape="standard"
              size="medium"
              tone="neutral"
              onClick={onEditClick}
            >
              <UIIcon src={EditLine} />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  },
);

export const UserProfileAvatarImage: React.FC<{
  displayName: string;
  profilePhotoUrl: string;
  size?: AvatarSizes;
}> = ({ displayName, profilePhotoUrl, size = "standard" }) => {
  const initials = getInitials(displayName);
  return (
    <Avatar
      alt={displayName}
      src={profilePhotoUrl}
      size={size}
      hasImageBackgroundColor={false}
      hasNoOutline
    >
      {initials}
    </Avatar>
  );
};

UserProfileAvatar.displayName = "UserProfileAvatar";
